import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import jwt_decode from 'jwt-decode';
// Components
import Florists from 'components/orders/Florists';
import ProdJobsList from 'components/productionJobs/ProdJobsList';
import LoadingOverlay from 'components/common/LoadingOverlay';
import RestrictedSection from 'components/common/RestrictedSection';
import ViewToggle from 'components/common/ViewToggle';
import SearchIcon from 'assets/images/svg/SearchIcon';
// Helpers
import {
  convertQueryParamsIntoObject,
  convertObjectIntoQueryParams,
  formatDateTime,
} from 'constants/Helpers';
import { useOnScreen } from 'components/common/hooks/useOnScreen';
import moment from 'moment';
// Selectors
import { getIsLoaderOpen } from 'store/app/AppSelectors';
import { getDeliveryData } from 'store/orders/OrdersListSelectors';
// API
import TimeSlotsService from 'services/TimeSlotsService';
// Actions
import { setLoaderOpenTrue, setLoaderOpenFalse } from 'store/app/AppActions';
import { getFloristsData } from 'store/orders/OrdersListActions';
// urls
import { getProductionJobsPageUrl } from 'constants/AppUrls';
// Localstorage
import { getFromLocalStorage } from 'helpers/LocalStorage';

const ProductionJobsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const ref = useRef();
  const deliveryDate = useSelector((state) => getDeliveryData({ state }));
  const [counter, setCounter] = useState(1);
  const isLoaderOpen = useSelector((state) => getIsLoaderOpen({ state }));
  const queryObj = location.search ? convertQueryParamsIntoObject(location.search) : {};
  const onScreen = useOnScreen(ref, '200px');
  const [productionJobsList, setProductionJobsList] = useState([]);
  const [pageCount, setPageCount] = useState(1);
  const [searchText, setSearchText] = useState('');

  const [initialParams, setInitialParams] = useState({
    page: 1,
    creationDate: formatDateTime(deliveryDate),
    floristId: -1,
    searchKey: '',
  });
  const [currentView, setCurrentView] = useState(2);

  const fetchMoreData = async (params, page) => {
    let clonedParams = {
      ...params,
      page: counter,
      searchKey: searchText?.trim() || '',
    };
    if (page) {
      setCounter(1);
      clonedParams = {
        ...params,
        page: 1,
        searchKey: searchText?.trim() || '',
      };
    }

    if (page || counter <= pageCount) {
      dispatch(setLoaderOpenTrue());
      try {
        const res = await TimeSlotsService.getProductionJobsList(clonedParams);

        setProductionJobsList((prevList) =>
          page || counter === 1 ? res?.data?.data : [...prevList, ...res?.data?.data]
        );

        setPageCount(res?.data?.pageCount);

        navigate({
          pathname: '/prod-jobs',
          search: convertObjectIntoQueryParams(clonedParams),
        });

        setCounter((prev) => prev + 1);
      } catch (err) {
      } finally {
        dispatch(setLoaderOpenFalse());
      }
    }
  };

  const handleSearchProductionJob = (e) => {
    e.preventDefault();
    const updatedParams = {
      ...initialParams,
      searchKey: searchText?.trim() || '',
      page: 1,
    };
    setInitialParams(updatedParams);
    setCounter(1);
    setProductionJobsList([]);
    fetchMoreData(updatedParams, 1);
  };

  const fetchData = (page) => {
    setInitialParams(queryObj);
    return fetchMoreData(queryObj, page);
  };

  const onViewChange = (vIndx, vURL) => {
    setCurrentView(vIndx);

    const floristId =
      vIndx === 1
        ? getFromLocalStorage('floristId') ?? jwt_decode(getFromLocalStorage('token'))?.id
        : -1;

    const updatedParams = {
      ...initialParams,
      floristId,
      page: 1,
    };

    setInitialParams(updatedParams);
    setProductionJobsList([]); // clear existing jobs
    setCounter(1);
    fetchMoreData(updatedParams, 1);

    navigate({
      pathname: vURL,
      search: convertObjectIntoQueryParams({
        creationDate: queryObj?.creationDate,
      }),
    });
  };

  const fetchFlorists = async () => {
    dispatch(
      getFloristsData({
        deliveryDate: queryObj.creationDate,
      })
    );
  };

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMyTasks = () => {
    return getProductionJobsPageUrl();
  };

  const handleAllTasks = () => {
    return getProductionJobsPageUrl();
  };

  return (
    <>
      {isLoaderOpen && <LoadingOverlay />}
      <div className="sort-group-wrapper">
        <div className="slot-date-wrapper">
          <h5 className="header-selected-date">Creation Date</h5>
          <h5 className="header-selected-date">
            {moment(queryObj?.creationDate).format('dddd ll')}
          </h5>
        </div>
        <form className="search-form" onSubmit={handleSearchProductionJob}>
          <input
            id="search-header-input"
            placeholder="Search by Production Job ID"
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText}
          />
          <SearchIcon />
        </form>
      </div>
      <ViewToggle activeView={currentView} onViewChange={onViewChange}>
        <div label="My Tasks" url={handleMyTasks()} viewIndex={1} />
        <div label="All Production Jobs" url={handleAllTasks()} viewIndex={2} />
      </ViewToggle>
      <RestrictedSection action="view_florists" page="florist_orders">
        <Florists
          initialParams={initialParams}
          setCounter={setCounter}
          fetchFlorists={fetchFlorists}
        />
      </RestrictedSection>
      <ProdJobsList
        ordersListData={productionJobsList}
        fetchMoreData={fetchData}
        fetchFlorists={fetchFlorists}
        initialParams={initialParams}
        onScreen={onScreen}
      />
      <div ref={ref} />
    </>
  );
};

export default ProductionJobsPage;
